// A branded, promise-based confirm + prompt dialog to replace window.confirm()
// and window.prompt() everywhere — native browser dialogs are banned (they look
// broken, can't be styled, and block the event loop).
//
// Usage:
//   const confirm = useConfirm();
//   if (!(await confirm({ message: 'Remove "X"?', danger: true, confirmLabel: 'Remove' }))) return;
//
//   const choose = useChoice();
//   const how = await choose({ title: 'Two device ids', choices: [
//     { value: 'additional', label: 'They use both' },
//     { value: 'replace',    label: 'This one replaced the old' },
//   ]});   // null = cancelled
//
//   const promptText = usePrompt();
//   const reason = await promptText({ title: 'Cancel assignment', label: 'Cancellation reason', required: true });
//   if (reason == null) return; // cancelled
//
// Mount <ConfirmProvider> once near the app root (above the router).
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

export interface ConfirmOptions {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for destructive actions (delete/remove). */
  danger?: boolean;
}

export interface PromptOptions {
  title?: string;
  message?: React.ReactNode;
  /** Field label shown above the input. */
  label?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** Disable submit until non-blank text is entered. */
  required?: boolean;
  /** Textarea instead of a single-line input (reasons, notes). */
  multiline?: boolean;
  /** 'date' asks for a calendar date (value YYYY-MM-DD). Default text. */
  inputType?: 'text' | 'date';
  /** Initial text (e.g. a link to copy). */
  defaultValue?: string;
  /** Minimum trimmed length. The requirement is SHOWN in the dialog and
   *  submit stays disabled until it is met — never a silent no-op. */
  minLength?: number;
  /** Text for the requirement line; {{n}} is replaced. Default English. */
  minLengthHint?: string;
}

/**
 * One option in a choice dialog.
 *
 * `description` is not decoration. A choice dialog exists because there are
 * three or more outcomes and none of them is obviously right — the sentence
 * under each label is what makes picking one a decision rather than a guess.
 */
export interface ChoiceOption {
  value: string;
  label: string;
  description?: React.ReactNode;
  /** Red, for the option that discards or retires something. */
  danger?: boolean;
}

export interface ChoiceOptions {
  title?: string;
  message?: React.ReactNode;
  choices: ChoiceOption[];
  cancelLabel?: string;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
type ChoiceFn = (opts: ChoiceOptions) => Promise<string | null>;
type PromptFn = (opts: PromptOptions) => Promise<string | null>;

const ConfirmContext = createContext<{ confirm: ConfirmFn; prompt: PromptFn; choose: ChoiceFn } | null>(null);

/** Returns a confirm(options) => Promise<boolean>. Resolves true on confirm. */
export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx.confirm;
};

/**
 * Returns a choose(options) => Promise<string | null> (null = cancelled).
 *
 * Use it instead of a confirm when there are three or more real outcomes.
 * Squeezing a third outcome into a two-button confirm is how somebody ends
 * up picking "replace" because it was the only button that was not Cancel.
 */
export const useChoice = (): ChoiceFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useChoice must be used within a ConfirmProvider');
  return ctx.choose;
};

/** Returns a prompt(options) => Promise<string | null> (null = cancelled). */
export const usePrompt = (): PromptFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('usePrompt must be used within a ConfirmProvider');
  return ctx.prompt;
};

/**
 * Like usePrompt, but null (instead of throwing) when no ConfirmProvider is
 * mounted — for components also rendered standalone (isolated tests, embeds)
 * where the action that needs the prompt should simply not be offered.
 */
export const usePromptIfAvailable = (): PromptFn | null => useContext(ConfirmContext)?.prompt ?? null;

/** confirm() when a ConfirmProvider is mounted, else null (the caller then
 *  skips the question rather than crashing a surface rendered standalone). */
export const useConfirmIfAvailable = (): ConfirmFn | null => useContext(ConfirmContext)?.confirm ?? null;

type DialogState =
  | { kind: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'prompt'; opts: PromptOptions; resolve: (v: string | null) => void }
  | { kind: 'choice'; opts: ChoiceOptions; resolve: (v: string | null) => void };

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DialogState | null>(null);
  const [text, setText] = useState('');

  const confirm = useCallback<ConfirmFn>(
    (opts) => new Promise<boolean>((resolve) => setState({ kind: 'confirm', opts, resolve })),
    [],
  );
  const prompt = useCallback<PromptFn>(
    (opts) =>
      new Promise<string | null>((resolve) => {
        setText(opts.defaultValue ?? '');
        setState({ kind: 'prompt', opts, resolve });
      }),
    [],
  );

  const choose = useCallback<ChoiceFn>(
    (opts) => new Promise<string | null>((resolve) => setState({ kind: 'choice', opts, resolve })),
    [],
  );

  /** Resolves a choice dialog with the option picked, or null for cancelled. */
  const closeChoice = useCallback((value: string | null) => {
    setState((s) => {
      if (s?.kind === 'choice') s.resolve(value);
      return null;
    });
  }, []);

  const close = useCallback(
    (ok: boolean) => {
      setState((s) => {
        if (s?.kind === 'confirm') s.resolve(ok);
        else if (s?.kind === 'prompt') s.resolve(ok ? text : null);
        // A choice has no "the" answer, so a stray close() cancels it rather
        // than silently picking the first option.
        else if (s?.kind === 'choice') s.resolve(null);
        return null;
      });
    },
    [text],
  );

  const promptMin = state?.kind === 'prompt' ? state.opts.minLength ?? 0 : 0;
  const tooShort = state?.kind === 'prompt' && promptMin > 0 && text.trim().length < promptMin;
  const submitDisabled = state?.kind === 'prompt' && ((!!state.opts.required && !text.trim()) || tooShort);

  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
      // Deliberately no Enter shortcut on a choice: there is no default
      // among three outcomes, and guessing one is the whole hazard.
      else if (e.key === 'Enter' && state.kind !== 'choice' && !submitDisabled
        && !(state.kind === 'prompt' && state.opts.multiline)) close(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, close, submitDisabled]);

  const opts = state?.opts as (ConfirmOptions | PromptOptions | ChoiceOptions) | undefined;

  return (
    <ConfirmContext.Provider value={{ confirm, prompt, choose }}>
      {children}
      {state && opts && (
        // Rendered as a Radix dialog layer so it stacks ABOVE any open Radix
        // dialog: a confirm raised from inside an edit dialog used to sit
        // outside that dialog's layer, where clicks and focus were blocked —
        // the page froze until a reload. Radix pauses the outer dialog's focus
        // trap and outside-click guard while this one is open. Escape/Enter
        // stay with our own window listener below.
        <DialogPrimitive.Root open>
        <DialogPrimitive.Portal>
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-charcoal/60 p-4 backdrop-blur-sm pointer-events-auto"
          data-kp-confirm=""
          onClick={() => (state.kind === 'choice' ? closeChoice(null) : close(false))}
        >
          <DialogPrimitive.Content
            asChild
            aria-describedby={undefined}
            onEscapeKeyDown={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
          <div
            role="dialog"
            aria-modal="true"
            className={`w-full rounded-2xl border border-border bg-card p-6 shadow-2xl ${
              // A choice stacks options with a sentence each; max-w-sm wraps
              // every one of them to four lines.
              state.kind === 'choice' ? 'max-w-md' : 'max-w-sm'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <DialogPrimitive.Title asChild>
            <h2 className="text-base font-bold text-foreground">
              {opts.title || ((opts as ConfirmOptions).danger ? 'Are you sure?' : 'Please confirm')}
            </h2>
            </DialogPrimitive.Title>
            {(opts as ConfirmOptions).message ? (
              <div className="mt-2 text-sm leading-relaxed text-muted-foreground">{(opts as ConfirmOptions).message}</div>
            ) : null}
            {state.kind === 'choice' && (
              <div className="mt-4 space-y-2">
                {state.opts.choices.map((ch, i) => (
                  <button
                    key={ch.value}
                    type="button"
                    autoFocus={i === 0}
                    onClick={() => closeChoice(ch.value)}
                    className={`w-full rounded-lg border px-4 py-3 text-left transition-colors ${
                      ch.danger
                        ? 'border-red-300 hover:bg-red-50 dark:border-red-500/40 dark:hover:bg-red-500/[0.08]'
                        : 'border-border hover:bg-muted'
                    }`}
                  >
                    <span className={`block text-sm font-semibold ${ch.danger ? 'text-red-600' : 'text-foreground'}`}>
                      {ch.label}
                    </span>
                    {ch.description ? (
                      <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                        {ch.description}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            )}
            {state.kind === 'prompt' && (
              <div className="mt-3 space-y-1">
                {state.opts.label && (
                  <label className="text-xs font-medium text-foreground">
                    {state.opts.label}
                    {state.opts.required ? ' *' : ''}
                  </label>
                )}
                {state.opts.multiline ? (
                  <textarea
                    autoFocus
                    rows={3}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={state.opts.placeholder}
                    className="w-full rounded-md border border-border bg-background px-2.5 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                ) : (
                  <input
                    autoFocus
                    type={state.opts.inputType ?? 'text'}
                    aria-label={state.opts.label}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onFocus={(e) => { if (state.opts.defaultValue) e.currentTarget.select(); }}
                    placeholder={state.opts.placeholder}
                    className="w-full h-9 rounded-md border border-border bg-background px-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                )}
                {promptMin > 0 && (
                  <p className={`text-xs ${tooShort ? 'text-warning' : 'text-muted-foreground'}`} role="status">
                    {(state.opts.minLengthHint || 'At least {{n}} characters').replace('{{n}}', String(promptMin))}
                    {` (${text.trim().length}/${promptMin})`}
                  </p>
                )}
              </div>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => (state.kind === 'choice' ? closeChoice(null) : close(false))}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                {opts.cancelLabel || 'Cancel'}
              </button>
              {state.kind !== 'choice' && <button
                type="button"
                autoFocus={state.kind === 'confirm'}
                disabled={submitDisabled}
                onClick={() => close(true)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  (opts as ConfirmOptions).danger ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-500 hover:bg-orange-600'
                }`}
              >
                {(opts as ConfirmOptions).confirmLabel || 'Confirm'}
              </button>}
            </div>
          </div>
          </DialogPrimitive.Content>
        </div>
        </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      )}
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
